import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Hvor mange personalmøter pr år pr butikk (mål)
const TARGET_MEETINGS_PER_YEAR = 4;

// ✅ Lønnsomhet-kalkyle
const GROSS_MARGIN = 0.4; // 40% bruttofortjeneste

// Hjelper: hent år fra ISO dato
const getYear = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? null : d.getFullYear();
};

// Valgfri: tell BOTH som begge butikker (for summering)
function addCountByScope(map, scope, add, countBothAsBoth) {
  if (!scope) return;
  if (scope === "BOTH" && countBothAsBoth) {
    map.ROYKEN = (map.ROYKEN || 0) + add;
    map.SLEMMESTAD = (map.SLEMMESTAD || 0) + add;
    return;
  }
  map[scope] = (map[scope] || 0) + add;
}

// Returnerer true hvis event “tilhører” scope gitt countBothAsBoth
const matchesScope = (evScope, scope, countBothAsBoth) => {
  const s = evScope || "BOTH";

  if (countBothAsBoth) {
    if (scope === "ROYKEN") return s === "ROYKEN" || s === "BOTH";
    if (scope === "SLEMMESTAD") return s === "SLEMMESTAD" || s === "BOTH";
    if (scope === "BOTH") return s === "BOTH";
    return false;
  }

  return s === scope;
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("nb-NO", { dateStyle: "medium", timeStyle: "short" });
};

const formatMoney = (n) => {
  const num = Number(n);
  if (isNaN(num)) return "";
  return `${Math.round(num)} kr`;
};

// 👇 Definer hva som er kurs (type KURS)
const isCourseEvent = (ev) => {
  const t = ev?.locationHint?.eventType || ev?.tags?.[0];
  return t === "KURS";
};

// 👇 Definer personalmøte (intern + ikke kurs)
const isPersonalMeeting = (ev) => {
  if (ev?.category !== "INTERN") return false;
  return !isCourseEvent(ev);
};

// 👇 Definer stand
const isStandEvent = (ev) => {
  const t = ev?.locationHint?.eventType || ev?.tags?.[0];
  return ev?.category === "STAND" || t === "STAND";
};

// 👇 "passert dato" sjekk (bruker end hvis finnes ellers start)
const isPastEvent = (ev) => {
  const end = ev?.end ? new Date(ev.end) : null;
  const start = ev?.start ? new Date(ev.start) : null;
  const d = end && !isNaN(end) ? end : start;
  if (!d || isNaN(d)) return false;
  return d.getTime() < Date.now();
};

// ✅ Hent timer brukt på stand (støtter flere mulige feltnavn)
const getStandHours = (ev) => {
  // anbefalt: participants.hoursById (Map eller object)
  const h1 = ev?.participants?.hoursById;
  // fallback: root-felt
  const h2 = ev?.participantHoursById;
  // fallback: locationHint
  const h3 = ev?.locationHint?.participantHoursById;

  const hoursById = h1 || h2 || h3 || {};

  // hoursById kan være Map (mongoose) eller plain object
  const obj =
    typeof hoursById?.forEach === "function" &&
    typeof hoursById?.entries === "function"
      ? Object.fromEntries(hoursById.entries())
      : hoursById;

  return Object.values(obj || {}).reduce((acc, v) => {
    const n = Number(v);
    return acc + (isNaN(n) ? 0 : n);
  }, 0);
};

const VAT_RATE = 0.25;
const SLEMMESTAD_RENT_RATE = 0.035; // 3,5% av brutto omsetning (inkl mva)


const calcStandProfit = (turnoverIncl, hours, storeScope) => {
  const incl = Number(turnoverIncl) || 0;
  const hrs = Number(hours) || 0;

  // Omsetning ex mva (grunnlag for varekost/DB)
  const revenue = incl / (1 + VAT_RATE);

  // DB vare (40% av ex mva)
  const dbVare = revenue * GROSS_MARGIN;
  const cogs = revenue - dbVare;

  // Lønnskost (inkl AGA/feriepenger/OTP)
  const labor = calcLaborCost(hrs);
  const laborCost = labor.total;

  // ✅ Husleie kun for Slemmestad, beregnet av brutto omsetning (inkl mva)
  const rentCost =
    (storeScope || "BOTH") === "SLEMMESTAD" ? incl * SLEMMESTAD_RENT_RATE : 0;

  // ✅ Fortjeneste etter lønn + ev. husleie
  const profitAfterLabor = dbVare - laborCost;
  const profitAfterLaborAndRent = profitAfterLabor - rentCost;

  return {
    turnoverIncl: incl,
    revenue,
    dbVare,
    cogs,
    hours: hrs,

    laborCost,
    laborBreakdown: labor,

    rentCost, // 👈 NY
    profitAfterLabor,
    profitAfterLaborAndRent, // 👈 NY (anbefalt å vise som “Fortjeneste”)
  };
};



// --- Lønnskost-satser (juster etter behov) ---
const BASE_HOURLY_WAGE = 180;      // timelønn
const AGA_RATE = 0.141;            // arbeidsgiveravgift (ofte 14,1% i sone 1)
const HOLIDAY_PAY_RATE = 0.102;    // feriepenger (10,2% normalt, 12% for 5 uker)
const OTP_RATE = 0.02;             // OTP/pensjon (min 2%) - valgfri, sett 0 hvis du ikke vil

const calcLaborCost = (hours) => {
  const hrs = Number(hours) || 0;

  const wage = hrs * BASE_HOURLY_WAGE;                  // grunnlønn
  const holidayPay = wage * HOLIDAY_PAY_RATE;           // feriepenger
  const aga = (wage + holidayPay) * AGA_RATE;           // AGA på lønn + feriepenger
  const otp = wage * OTP_RATE;                          // OTP (enkelt estimat)

  return {
    wage,
    holidayPay,
    aga,
    otp,
    total: wage + holidayPay + aga + otp,
  };
};



export default function EventStatsPage() {
  const navigate = useNavigate();

  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtre
  const [selectedYear, setSelectedYear] = useState("ALLE");
  const [countBothAsBoth, setCountBothAsBoth] = useState(true);

  // For stands: gruppevalg (for omsetnings-visning)
  const [standGroupBy, setStandGroupBy] = useState("BUTIKK"); // BUTIKK | FORENING

  // Drawer / modal
  const [drawer, setDrawer] = useState({
    open: false,
    title: "",
    subtitle: "",
    items: [],
    kind: "GENERIC", // PERSONALMOTE | KURS | STAND
  });

  const openDrawer = ({ title, subtitle = "", items = [], kind = "GENERIC" }) => {
    setDrawer({ open: true, title, subtitle, items, kind });
  };

  const closeDrawer = () => {
    setDrawer({ open: false, title: "", subtitle: "", items: [], kind: "GENERIC" });
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/events`);
        const data = await res.json();
        setRawEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Feil ved henting av events:", e);
        setRawEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const years = useMemo(() => {
    const ys = new Set();
    rawEvents.forEach((ev) => {
      const y = getYear(ev.start);
      if (y) ys.add(y);
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [rawEvents]);

  const filteredEvents = useMemo(() => {
    if (selectedYear === "ALLE") return rawEvents;
    const y = Number(selectedYear);
    return rawEvents.filter((ev) => getYear(ev.start) === y);
  }, [rawEvents, selectedYear]);

  // --- Personalmøter: planlagte + done ---
  const plannedPersonalMeetings = useMemo(() => {
    return filteredEvents
      .filter((ev) => isPersonalMeeting(ev) && ev.status === "PLANNED")
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredEvents]);

  const donePersonalMeetings = useMemo(() => {
    return filteredEvents
      .filter((ev) => isPersonalMeeting(ev) && ev.status === "DONE")
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredEvents]);

  // --- Kurs: planlagte + done ---
  const plannedCourses = useMemo(() => {
    return filteredEvents
      .filter((ev) => ev.category === "INTERN" && isCourseEvent(ev) && ev.status === "PLANNED")
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredEvents]);

  const doneCourses = useMemo(() => {
    return filteredEvents
      .filter((ev) => ev.category === "INTERN" && isCourseEvent(ev) && ev.status === "DONE")
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredEvents]);

  // --- STANDS: planlagte + done (ALLE stands) ---
  const plannedStands = useMemo(() => {
    return filteredEvents
      .filter((ev) => isStandEvent(ev) && ev.status === "PLANNED")
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredEvents]);

  const doneStands = useMemo(() => {
    return filteredEvents
      .filter((ev) => isStandEvent(ev) && ev.status === "DONE")
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredEvents]);

  // --- Alle events med “salg utenfor butikk” + turnover (uavhengig av type) ---
  const outOfStoreStands = useMemo(() => {
    return filteredEvents
      .filter((ev) => {
        const lh = ev.locationHint || {};
        return (
          lh.outOfStoreSale === true &&
          typeof lh.outOfStoreTurnover === "number" &&
          !isNaN(lh.outOfStoreTurnover)
        );
      })
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredEvents]);

  // ✅ Lønnsomhet for stands med omsetning (pr stand + totalsummer)
  const standProfit = useMemo(() => {
    const rows = outOfStoreStands.map((ev) => {
      const turnover = ev.locationHint?.outOfStoreTurnover || 0;
      const hours = getStandHours(ev);
const p = calcStandProfit(turnover, hours, ev.storeScope);

      return {
        _id: ev._id,
        title: ev.title,
        start: ev.start,
        end: ev.end,
        storeScope: ev.storeScope || "BOTH",
        party: ev.locationHint?.externalPartyName || "Ukjent",
        status: ev.status || "PLANNED",
        ...p,
      };
    });

const total = rows.reduce(
  (acc, r) => {
    acc.turnoverIncl += r.turnoverIncl;
    acc.revenue += r.revenue;
    acc.dbVare += r.dbVare;
    acc.cogs += r.cogs;
    acc.hours += r.hours;
    acc.laborCost += r.laborCost;
    acc.profitAfterLabor += r.profitAfterLabor;
    acc.rentCost += r.rentCost;
    acc.profitAfterLaborAndRent += r.profitAfterLaborAndRent;

    return acc;
  },
{
  turnoverIncl: 0,
  revenue: 0,
  dbVare: 0,
  cogs: 0,
  hours: 0,
  laborCost: 0,
  rentCost: 0,
  profitAfterLabor: 0,
  profitAfterLaborAndRent: 0,
}

);


total.dbVarePct = total.revenue > 0 ? total.dbVare / total.revenue : 0;

total.profitAfterPct =
  total.revenue > 0 ? total.profitAfterLaborAndRent / total.revenue : 0;




    // pr klubb
const byParty = {};
rows.forEach((r) => {
  if (!byParty[r.party]) {
byParty[r.party] = {
  revenue: 0,
  dbVare: 0,
  cogs: 0,
  hours: 0,
  laborCost: 0,
  dbAfterLabor: 0,
};

  }
  const a = byParty[r.party];
  a.revenue += r.revenue;
  a.dbVare += r.dbVare;
  a.cogs += r.cogs;
  a.hours += r.hours;
a.laborCost += r.laborCost;
a.rentCost += r.rentCost;
a.profitAfterLabor += r.profitAfterLabor;
a.profitAfterLaborAndRent += r.profitAfterLaborAndRent;

});

Object.values(byParty).forEach((a) => {
  a.dbVarePct = a.revenue > 0 ? a.dbVare / a.revenue : 0;
a.profitAfterPct = a.revenue > 0 ? a.profitAfterLaborAndRent / a.revenue : 0;
});


    return { rows, total, byParty };
  }, [outOfStoreStands]);

  // 1) Statistikk: personalmøter + kurs (planlagt + done)
  const internalStats = useMemo(() => {
    const meetingsPlannedByScope = { ROYKEN: 0, SLEMMESTAD: 0, BOTH: 0 };
    const meetingsDoneByScope = { ROYKEN: 0, SLEMMESTAD: 0, BOTH: 0 };

    const coursesPlannedByScope = { ROYKEN: 0, SLEMMESTAD: 0, BOTH: 0 };
    const coursesDoneByScope = { ROYKEN: 0, SLEMMESTAD: 0, BOTH: 0 };

    plannedPersonalMeetings.forEach((ev) =>
      addCountByScope(meetingsPlannedByScope, ev.storeScope || "BOTH", 1, countBothAsBoth)
    );
    donePersonalMeetings.forEach((ev) =>
      addCountByScope(meetingsDoneByScope, ev.storeScope || "BOTH", 1, countBothAsBoth)
    );

    plannedCourses.forEach((ev) =>
      addCountByScope(coursesPlannedByScope, ev.storeScope || "BOTH", 1, countBothAsBoth)
    );
    doneCourses.forEach((ev) =>
      addCountByScope(coursesDoneByScope, ev.storeScope || "BOTH", 1, countBothAsBoth)
    );

    return {
      meetings: { planned: meetingsPlannedByScope, done: meetingsDoneByScope },
      courses: { planned: coursesPlannedByScope, done: coursesDoneByScope },
    };
  }, [plannedPersonalMeetings, donePersonalMeetings, plannedCourses, doneCourses, countBothAsBoth]);

  // 2) Stands (ALLE): planlagt + done pr butikk
  const standStatsByScope = useMemo(() => {
    const planned = { ROYKEN: 0, SLEMMESTAD: 0, BOTH: 0 };
    const done = { ROYKEN: 0, SLEMMESTAD: 0, BOTH: 0 };

    plannedStands.forEach((ev) => addCountByScope(planned, ev.storeScope || "BOTH", 1, countBothAsBoth));
    doneStands.forEach((ev) => addCountByScope(done, ev.storeScope || "BOTH", 1, countBothAsBoth));

    return { planned, done };
  }, [plannedStands, doneStands, countBothAsBoth]);

  // 3) Stands (ALLE): planlagt + done pr klubb/forening
  const standStatsByParty = useMemo(() => {
    const byParty = {};
    const all = [...plannedStands, ...doneStands];

    all.forEach((ev) => {
      const party = ev.locationHint?.externalPartyName || "Ukjent";
      if (!byParty[party]) byParty[party] = { planned: 0, done: 0 };
      if (ev.status === "DONE") byParty[party].done += 1;
      else byParty[party].planned += 1;
    });

    return byParty;
  }, [plannedStands, doneStands]);

  // 4) Stands med omsetning (summering pr butikk/klubb for "kort")
  const standOutOfStore = useMemo(() => {
    const stands = outOfStoreStands;

    if (standGroupBy === "FORENING") {
      const byParty = {};

      stands.forEach((ev) => {
        const party = ev.locationHint?.externalPartyName || "Ukjent";
        if (!byParty[party]) {
          byParty[party] = {
            count: 0,
            turnover: 0,
            scopes: { ROYKEN: 0, SLEMMESTAD: 0, BOTH: 0 },
          };
        }

        byParty[party].count += 1;
        byParty[party].turnover += ev.locationHint.outOfStoreTurnover || 0;

        if (!countBothAsBoth) {
          const s = ev.storeScope || "BOTH";
          byParty[party].scopes[s] = (byParty[party].scopes[s] || 0) + 1;
        } else {
          addCountByScope(byParty[party].scopes, ev.storeScope || "BOTH", 1, true);
        }
      });

      return { mode: "FORENING", byParty };
    }

    const byScope = {
      ROYKEN: { count: 0, turnover: 0 },
      SLEMMESTAD: { count: 0, turnover: 0 },
      BOTH: { count: 0, turnover: 0 },
    };

    stands.forEach((ev) => {
      const scope = ev.storeScope || "BOTH";
      const turnover = ev.locationHint.outOfStoreTurnover || 0;

      if (!countBothAsBoth) {
        byScope[scope].count += 1;
        byScope[scope].turnover += turnover;
      } else {
        if (scope === "BOTH") {
          byScope.ROYKEN.count += 1;
          byScope.ROYKEN.turnover += turnover;
          byScope.SLEMMESTAD.count += 1;
          byScope.SLEMMESTAD.turnover += turnover;
        } else {
          byScope[scope].count += 1;
          byScope[scope].turnover += turnover;
        }
      }
    });

    return { mode: "BUTIKK", byScope };
  }, [outOfStoreStands, standGroupBy, countBothAsBoth]);

  // ✅ Oppdater event i DB + local state (brukes av "Marker som gjennomført")
  const markEventDone = async (eventId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "DONE" }),
      });
      if (!res.ok) throw new Error(`Kunne ikke oppdatere (${res.status})`);
      const updated = await res.json();

      setRawEvents((prev) => prev.map((e) => (e._id === updated._id ? updated : e)));
      setDrawer((prev) => ({
        ...prev,
        items: prev.items.map((e) => (e._id === updated._id ? updated : e)),
      }));
    } catch (e) {
      alert(e.message || "Kunne ikke lagre gjennomført");
    }
  };

  // --- Åpne lister ---
  const openMeetingListForScope = (scope, label) => {
    const items = filteredEvents
      .filter((ev) => isPersonalMeeting(ev))
      .filter((ev) => matchesScope(ev.storeScope, scope, countBothAsBoth))
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    openDrawer({
      kind: "PERSONALMOTE",
      title: `Personalmøter – ${label}`,
      subtitle:
        selectedYear === "ALLE"
          ? `Alle år • ${items.length} treff`
          : `År ${selectedYear} • ${items.length} treff`,
      items,
    });
  };

  const openCourseListForScope = (scope, label) => {
    const items = filteredEvents
      .filter((ev) => ev.category === "INTERN" && isCourseEvent(ev))
      .filter((ev) => matchesScope(ev.storeScope, scope, countBothAsBoth))
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    openDrawer({
      kind: "KURS",
      title: `Kurs – ${label}`,
      subtitle:
        selectedYear === "ALLE"
          ? `Alle år • ${items.length} treff`
          : `År ${selectedYear} • ${items.length} treff`,
      items,
    });
  };

  // ✅ STANDS (ALLE): åpne liste pr scope
  const openStandListForScope = (scope, label) => {
    const items = filteredEvents
      .filter((ev) => isStandEvent(ev))
      .filter((ev) => matchesScope(ev.storeScope, scope, countBothAsBoth))
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    openDrawer({
      kind: "STAND",
      title: `Stands – ${label}`,
      subtitle:
        (selectedYear === "ALLE" ? "Alle år" : `År ${selectedYear}`) +
        ` • ${items.length} treff`,
      items,
    });
  };

  // ✅ STANDS (ALLE): åpne liste pr klubb/forening
  const openStandListForParty = (party) => {
    const items = filteredEvents
      .filter((ev) => isStandEvent(ev))
      .filter((ev) => (ev.locationHint?.externalPartyName || "Ukjent") === party)
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    openDrawer({
      kind: "STAND",
      title: `Stands – ${party}`,
      subtitle:
        (selectedYear === "ALLE" ? "Alle år" : `År ${selectedYear}`) +
        ` • ${items.length} treff`,
      items,
    });
  };

  // (Behold) Åpne liste for stands MED omsetning
  const openOutOfStoreStandListForScope = (scope, label) => {
    const items = outOfStoreStands.filter((ev) =>
      matchesScope(ev.storeScope, scope, countBothAsBoth)
    );
    const sum = items.reduce(
      (acc, ev) => acc + (ev.locationHint?.outOfStoreTurnover || 0),
      0
    );

    openDrawer({
      kind: "STAND",
      title: `Stands (salg utenfor butikk) – ${label}`,
      subtitle:
        (selectedYear === "ALLE" ? "Alle år" : `År ${selectedYear}`) +
        ` • ${items.length} treff • Sum omsetning: ${formatMoney(sum)}`,
      items,
    });
  };

  const openOutOfStoreStandListForParty = (party) => {
    const items = outOfStoreStands.filter(
      (ev) => (ev.locationHint?.externalPartyName || "Ukjent") === party
    );
    const sum = items.reduce(
      (acc, ev) => acc + (ev.locationHint?.outOfStoreTurnover || 0),
      0
    );

    openDrawer({
      kind: "STAND",
      title: `Stands (salg utenfor butikk) – ${party}`,
      subtitle:
        (selectedYear === "ALLE" ? "Alle år" : `År ${selectedYear}`) +
        ` • ${items.length} treff • Sum omsetning: ${formatMoney(sum)}`,
      items,
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-4">
        <p>Laster statistikk…</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-2xl font-bold">Årshjul – Statistikk</h2>
        <button
          className="rounded-md px-4 py-2 border border-gray-300 text-sm"
          onClick={() => navigate("/eventcalendar")}
        >
          Tilbake til kalender
        </button>
      </div>

      {/* Filtre */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">År</label>
          <select
            className="rounded-md border border-gray-300 p-2 bg-white"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="ALLE">Alle år</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={countBothAsBoth}
            onChange={(e) => setCountBothAsBoth(e.target.checked)}
          />
          Tell BOTH som begge butikker
        </label>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">
            Stands (omsetning) grupperes pr
          </label>
          <select
            className="rounded-md border border-gray-300 p-2 bg-white"
            value={standGroupBy}
            onChange={(e) => setStandGroupBy(e.target.value)}
          >
            <option value="BUTIKK">Butikk</option>
            <option value="FORENING">Forening/lag</option>
          </select>
        </div>
      </div>

      {/* Personalmøter */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold mb-3">Personalmøter</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard
            title="Røyken"
            value={`${internalStats.meetings.done.ROYKEN || 0} av ${TARGET_MEETINGS_PER_YEAR} gjennomført`}
            sub={`${internalStats.meetings.planned.ROYKEN || 0} av ${TARGET_MEETINGS_PER_YEAR} planlagt`}
            onClick={() => openMeetingListForScope("ROYKEN", "Røyken")}
          />
          <StatCard
            title="Slemmestad"
            value={`${internalStats.meetings.done.SLEMMESTAD || 0} av ${TARGET_MEETINGS_PER_YEAR} gjennomført`}
            sub={`${internalStats.meetings.planned.SLEMMESTAD || 0} av ${TARGET_MEETINGS_PER_YEAR} planlagt`}
            onClick={() => openMeetingListForScope("SLEMMESTAD", "Slemmestad")}
          />
          {!countBothAsBoth && (
            <StatCard
              title="Begge (BOTH)"
              value={`${internalStats.meetings.done.BOTH || 0} av ${TARGET_MEETINGS_PER_YEAR} gjennomført`}
              sub={`${internalStats.meetings.planned.BOTH || 0} av ${TARGET_MEETINGS_PER_YEAR} planlagt`}
              onClick={() => openMeetingListForScope("BOTH", "BOTH")}
            />
          )}
        </div>
      </div>

      {/* Kurs */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold mb-3">Kurs</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard
            title="Røyken"
            value={`${internalStats.courses.done.ROYKEN || 0} gjennomført`}
            sub={`${internalStats.courses.planned.ROYKEN || 0} planlagt`}
            onClick={() => openCourseListForScope("ROYKEN", "Røyken")}
          />
          <StatCard
            title="Slemmestad"
            value={`${internalStats.courses.done.SLEMMESTAD || 0} gjennomført`}
            sub={`${internalStats.courses.planned.SLEMMESTAD || 0} planlagt`}
            onClick={() => openCourseListForScope("SLEMMESTAD", "Slemmestad")}
          />
          {!countBothAsBoth && (
            <StatCard
              title="Begge (BOTH)"
              value={`${internalStats.courses.done.BOTH || 0} gjennomført`}
              sub={`${internalStats.courses.planned.BOTH || 0} planlagt`}
              onClick={() => openCourseListForScope("BOTH", "BOTH")}
            />
          )}
        </div>
      </div>

      {/* ✅ Stands (ALLE) */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold mb-3">
          Stands – planlagt og gjennomført
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard
            title="Røyken"
            value={`${standStatsByScope.done.ROYKEN || 0} gjennomført`}
            sub={`${standStatsByScope.planned.ROYKEN || 0} planlagt`}
            onClick={() => openStandListForScope("ROYKEN", "Røyken")}
          />
          <StatCard
            title="Slemmestad"
            value={`${standStatsByScope.done.SLEMMESTAD || 0} gjennomført`}
            sub={`${standStatsByScope.planned.SLEMMESTAD || 0} planlagt`}
            onClick={() => openStandListForScope("SLEMMESTAD", "Slemmestad")}
          />
          {!countBothAsBoth && (
            <StatCard
              title="Begge (BOTH)"
              value={`${standStatsByScope.done.BOTH || 0} gjennomført`}
              sub={`${standStatsByScope.planned.BOTH || 0} planlagt`}
              onClick={() => openStandListForScope("BOTH", "BOTH")}
            />
          )}
        </div>

        {/* ✅ Stands pr klubb/forening */}
        <div className="mt-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="text-sm text-gray-700 font-medium">
              Stands pr klubb/forening
            </div>
            <div className="text-xs text-gray-500">
              Klikk en rad for å se liste
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Klubb/forening</th>
                  <th className="py-2 pr-4">Planlagt</th>
                  <th className="py-2 pr-4">Gjennomført</th>
                  <th className="py-2 pr-4">Totalt</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(standStatsByParty)
                  .sort((a, b) => {
                    const aTot = (a[1].planned || 0) + (a[1].done || 0);
                    const bTot = (b[1].planned || 0) + (b[1].done || 0);
                    return bTot - aTot;
                  })
                  .map(([party, info]) => {
                    const planned = info.planned || 0;
                    const done = info.done || 0;
                    return (
                      <tr
                        key={party}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => openStandListForParty(party)}
                        title="Klikk for å se stands"
                      >
                        <td className="py-2 pr-4 font-medium">{party}</td>
                        <td className="py-2 pr-4">{planned}</td>
                        <td className="py-2 pr-4">{done}</td>
                        <td className="py-2 pr-4">{planned + done}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Stands utenfor butikk (med registrert omsetning) */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">
          Stands – salg utenfor butikk (med registrert omsetning)
        </h3>

        {/* ✅ Lønnsomhet – total */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
<StatCard
  title="Total omsetning"
  value={formatMoney(standProfit.total.turnoverIncl)}
  sub={`Omsetning ex mva: ${formatMoney(standProfit.total.revenue)}`}
/>

<StatCard
  title="Bemanning"
  value={`${standProfit.total.hours.toFixed(1)} t`}
sub={`Kost (inkl påslag): ${formatMoney(standProfit.total.laborCost)}`}
/>

<StatCard
  title="Fortjeneste"
  value={formatMoney(standProfit.total.profitAfterLaborAndRent)}
  sub={`DB vare (40%): ${formatMoney(standProfit.total.dbVare)} • Husleie: ${formatMoney(standProfit.total.rentCost)}`}
/>


        </div>

        {/* ✅ Tabell pr stand */}
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full text-sm">
<thead>
  <tr className="text-left border-b">
    <th className="py-2 pr-4">Dato</th>
    <th className="py-2 pr-4">Stand</th>
    <th className="py-2 pr-4">Klubb</th>
    <th className="py-2 pr-4">Butikk</th>
    <th className="py-2 pr-4">Omsetning (inkl mva)</th>
    <th className="py-2 pr-4">Timer</th>
    <th className="py-2 pr-4">Bem.kost</th>
    <th className="py-2 pr-4">Husleie (kun S)</th>
    <th className="py-2 pr-4">Varekost (ex mva)</th>
    <th className="py-2 pr-4">DB vare (40%)</th>
    <th className="py-2 pr-4">Fortjeneste</th>
  </tr>
</thead>


            <tbody>
              {standProfit.rows
                .slice()
                .sort((a, b) => new Date(a.start) - new Date(b.start))
                .map((r) => (
                  <tr
                    key={r._id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/events/${r._id}`)}
                    title="Klikk for å åpne event"
                  >
                    <td className="py-2 pr-4">{formatDate(r.start)}</td>
                    <td className="py-2 pr-4 font-medium">{r.title}</td>
                    <td className="py-2 pr-4">{r.party}</td>
                    <td className="py-2 pr-4">{r.storeScope}</td>
<td className="py-2 pr-4">{formatMoney(r.turnoverIncl)}</td>
<td className="py-2 pr-4">{r.hours.toFixed(1)}</td>
<td className="py-2 pr-4">{formatMoney(r.laborCost)}</td>
<td className="py-2 pr-4">{formatMoney(r.rentCost)}</td>
<td className="py-2 pr-4">{formatMoney(r.cogs)}</td>
<td className="py-2 pr-4">{formatMoney(r.dbVare)}</td>
<td className="py-2 pr-4">{formatMoney(r.profitAfterLaborAndRent)}</td>


                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Eksisterende visning (omsetning pr butikk/klubb) */}
        {standOutOfStore.mode === "BUTIKK" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <StatCard
              title="Røyken"
              value={`${standOutOfStore.byScope.ROYKEN.count} stk`}
              sub={`Omsetning: ${formatMoney(standOutOfStore.byScope.ROYKEN.turnover)}`}
              onClick={() => openOutOfStoreStandListForScope("ROYKEN", "Røyken")}
            />
            <StatCard
              title="Slemmestad"
              value={`${standOutOfStore.byScope.SLEMMESTAD.count} stk`}
              sub={`Omsetning: ${formatMoney(standOutOfStore.byScope.SLEMMESTAD.turnover)}`}
              onClick={() => openOutOfStoreStandListForScope("SLEMMESTAD", "Slemmestad")}
            />
            {!countBothAsBoth && (
              <StatCard
                title="Begge (BOTH)"
                value={`${standOutOfStore.byScope.BOTH.count} stk`}
                sub={`Omsetning: ${formatMoney(standOutOfStore.byScope.BOTH.turnover)}`}
                onClick={() => openOutOfStoreStandListForScope("BOTH", "BOTH")}
              />
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Forening/lag</th>
                  <th className="py-2 pr-4">Antall</th>
                  <th className="py-2 pr-4">Omsetning</th>
                  <th className="py-2 pr-4">Røyken</th>
                  <th className="py-2 pr-4">Slemmestad</th>
                  {!countBothAsBoth && <th className="py-2 pr-4">BOTH</th>}
                </tr>
              </thead>
              <tbody>
                {Object.entries(standOutOfStore.byParty)
                  .sort((a, b) => (b[1].turnover || 0) - (a[1].turnover || 0))
                  .map(([party, info]) => (
                    <tr
                      key={party}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => openOutOfStoreStandListForParty(party)}
                      title="Klikk for å se stands"
                    >
                      <td className="py-2 pr-4 font-medium">{party}</td>
                      <td className="py-2 pr-4">{info.count}</td>
                      <td className="py-2 pr-4">{formatMoney(info.turnover)}</td>
                      <td className="py-2 pr-4">{info.scopes.ROYKEN || 0}</td>
                      <td className="py-2 pr-4">{info.scopes.SLEMMESTAD || 0}</td>
                      {!countBothAsBoth && (
                        <td className="py-2 pr-4">{info.scopes.BOTH || 0}</td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer / Modal */}
      {drawer.open && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50"
          onClick={closeDrawer}
        >
          <div
            className="bg-white w-full md:max-w-3xl rounded-t-xl md:rounded-xl shadow-xl max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b p-4">
              <div>
                <h3 className="text-lg font-semibold">{drawer.title}</h3>
                {drawer.subtitle && (
                  <p className="text-xs text-gray-500 mt-1">{drawer.subtitle}</p>
                )}
              </div>
              <button
                className="text-sm px-3 py-2 border rounded-md"
                onClick={closeDrawer}
              >
                Lukk
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {drawer.items.length === 0 ? (
                <p className="text-sm text-gray-600">Ingen treff.</p>
              ) : (
                <div className="space-y-2">
                  {drawer.items.map((ev) => {
                    const lh = ev.locationHint || {};
                    const turnover =
                      typeof lh.outOfStoreTurnover === "number"
                        ? lh.outOfStoreTurnover
                        : null;

                    const meeting = isPersonalMeeting(ev);
                    const course = ev.category === "INTERN" && isCourseEvent(ev);
                    const stand = isStandEvent(ev) || lh.outOfStoreSale === true;

                    const showDoneButton =
                      (drawer.kind === "PERSONALMOTE" &&
                        meeting &&
                        isPastEvent(ev) &&
                        ev.status !== "DONE") ||
                      (drawer.kind === "KURS" &&
                        course &&
                        isPastEvent(ev) &&
                        ev.status !== "DONE") ||
                      (drawer.kind === "STAND" &&
                        stand &&
                        isPastEvent(ev) &&
                        ev.status !== "DONE");

                    const isDone = ev.status === "DONE";

                    // ✅ Lønnsomhet-linje i drawer for stand
                    const hours = stand ? getStandHours(ev) : 0;
const profit =
  stand && turnover != null
    ? calcStandProfit(turnover, hours, ev.storeScope)
    : null;


                    return (
                      <div key={ev._id} className="border rounded-md p-3">
                        <button
                          className="w-full text-left hover:bg-gray-50"
                          onClick={() => navigate(`/events/${ev._id}`)}
                          type="button"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-medium">{ev.title}</div>
                            <div className="text-xs text-gray-500">
                              {ev.storeScope || "BOTH"}
                            </div>
                          </div>

                          <div className="text-sm text-gray-600 mt-1">
                            {formatDate(ev.start)}
                            {ev.end && ev.end !== ev.start
                              ? ` – ${formatDate(ev.end)}`
                              : ""}
                          </div>

                          {ev.location && (
                            <div className="text-sm text-gray-500 mt-1">📍 {ev.location}</div>
                          )}

                          {drawer.kind === "STAND" && (
                            <div className="text-sm text-gray-700 mt-1">
                              {lh.externalPartyName
                                ? `Forening: ${lh.externalPartyName}`
                                : "Forening: Ukjent"}
                              {turnover != null
                                ? ` • Omsetning: ${formatMoney(turnover)}`
                                : ""}
{profit
  ? ` • Timer: ${profit.hours.toFixed(1)}`
    + ` • DB vare: ${formatMoney(profit.dbVare)}`
    + ` • Est. lønnskost: ${formatMoney(profit.laborCost)}`
    + ` • Husleie: ${formatMoney(profit.rentCost)}`
    + ` • Fortjeneste: ${formatMoney(profit.profitAfterLaborAndRent)}`
  : ""}


                            </div>
                          )}

                          <div className="text-xs text-gray-500 mt-1">
                            Status: {isDone ? "Gjennomført" : ev.status || "PLANNED"}
                          </div>
                        </button>

                        {showDoneButton && (
                          <div className="mt-3 flex items-center justify-between gap-3 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                            <div className="text-sm">
                              Datoen er passert. Marker som gjennomført når dette
                              er utført.
                            </div>
                            <button
                              type="button"
                              className="px-3 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
                              onClick={() => markEventDone(ev._id)}
                            >
                              Marker som gjennomført
                            </button>
                          </div>
                        )}

                        {isDone && (
                          <div className="mt-2 text-sm text-green-700">
                            ✅ Gjennomført
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, sub, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={[
        "border border-gray-200 rounded-lg p-4 text-left",
        clickable ? "hover:bg-gray-50 cursor-pointer" : "cursor-default",
      ].join(" ")}
      title={clickable ? "Klikk for å se liste" : ""}
    >
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-sm text-gray-600 mt-1">{sub}</div>}
      {clickable && <div className="text-xs text-gray-400 mt-2">Trykk for liste</div>}
    </button>
  );
}
